import { Button, InputGroup, Link, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { IStatusConfig, StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useDownload, useHasRole, useAppToast, formatPhone } from '../../../helpers'
import { JobApplicationService } from '../../../services/JobApplication/JobApplicationService'
import {
  JobApplication,
  JobApplicationComplete,
  JobApplicationPositions,
  JobApplicationRequisitionOption,
  JobApplicationStatuses,
  SalaryRange
} from '../../../services/JobApplication/types'
import { SalaryRangeService } from '../../../services/SalaryRange/SalaryRangeService'
import { JobApplicationFormModal } from './JobApplicationFormModal'
import { JobApplicationViewModal } from './JobApplicationViewModal'
import { vacancyLabel } from './vacancyLabel'

export const statusInfo: Record<JobApplicationStatuses, IStatusConfig> = {
  new: {
    label: 'Não Visualizado',
    colorScheme: 'gray'
  },
  qualified: {
    label: 'Qualificado',
    colorScheme: 'green'
  },
  hired: {
    label: 'Contratado',
    colorScheme: 'blue'
  },
  incompatible: {
    label: 'Incompatível',
    colorScheme: 'red'
  },
  positive_screening: {
    label: 'Triagem Positiva',
    colorScheme: 'yellow'
  },
  process_in_progress: {
    label: 'Processo em Andamento',
    colorScheme: 'cyan'
  }
}

/** `length` fica de fora: é o tamanho de página escolhido no Paginator, não um filtro. */
const INITIAL_FILTERS = {
  search: '',
  status: 'new',
  position: '',
  area: '',
  salary_range_id: '',
  requisitionId: null as number | null
}

export default function JobApplicationPage() {
  const service = new JobApplicationService()
  const salaryRangeService = new SalaryRangeService()
  const [isDeleting, setIsDeleting] = useState(false)
  const toast = useAppToast()
  const [jobApplications, setJobApplications] = useState<JobApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [positions, setPositions] = useState<JobApplicationPositions>([])
  const [requisitions, setRequisitions] = useState<JobApplicationRequisitionOption[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [salaryRanges, setSalaryRanges] = useState<SalaryRange[]>([])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [detailsDeleteDialog, setDetailsDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<JobApplication>()
  // "Candidatos" of an RP (Vagas de Emprego) opens here with `?rp={id}`, every status included.
  const [filters, setFilters] = useState(() => {
    const requisitionId = Number(searchParams.get('rp')) || null
    return {
      length: DEFAULT_PAGE_SIZE,
      ...INITIAL_FILTERS,
      ...(requisitionId ? { requisitionId, status: '' } : {})
    }
  })
  const isDirty =
    filters.search !== '' ||
    filters.status !== 'new' ||
    filters.position !== '' ||
    filters.area !== '' ||
    filters.salary_range_id !== '' ||
    filters.requisitionId !== null
  const [page, setPage] = useState(1)

  // The `?rp=` that opened the page stops applying once the RP filter changes (or a reload would bring it back).
  const dropRpParam = () => {
    if (!searchParams.has('rp')) return
    const next = new URLSearchParams(searchParams)
    next.delete('rp')
    setSearchParams(next, { replace: true })
  }
  const [totalPages, setTotalPages] = useState(1)
  const prevFiltersRef = useRef(filters)
  const didMount = useRef(false)
  const [updatingStatus, setUpdatingStatus] = useState<number[]>([])
  const [isEditingStatus, setIsEditingStatus] = useState<number[]>([])
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<JobApplicationComplete>()
  const { isDownloading, download } = useDownload()
  // O botão é por linha, então o estado do hook precisa saber qual delas está baixando.
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const hasRole = useHasRole()
  const permissions = {
    addCandidate: hasRole('add_candidate'),
    editCandidate: hasRole('edit_candidate'),
    deleteCandidate: hasRole('delete_candidate')
  }
  const filteredPosition = positions.find((position) => position.name === filters.position)

  useEffect(() => {
    // Sem `refreshList()` aqui: o efeito de `[filters, page]` abaixo já busca na montagem,
    // e chamar os dois dava duas requisições ao abrir a tela.
    service.positions().then((response) => setPositions(response))
    service
      .requisitions()
      .then(setRequisitions)
      .catch((error) => toast.error('Erro ao carregar as RPs', error))
    salaryRangeService.list().then((response) => setSalaryRanges(response))
  }, [])

  useEffect(() => {
    const filtersChanged = prevFiltersRef.current !== filters
    prevFiltersRef.current = filters

    if (filtersChanged && page !== 1) {
      setPage(1)
      return
    }
    refreshList()
  }, [filters, page])

  // Recarrega quando o modal de cadastro fecha. O guarda de primeira renderização é o mesmo
  // padrão de TeacherPage/EventPage: sem ele, este efeito também busca na montagem, e a tela
  // abria com duas requisições de listagem.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }
    if (!isCreateModalOpen) {
      refreshList()
    }
  }, [isCreateModalOpen])

  const handleDelete = async () => {
    if (!targetRecord) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(targetRecord.id)
      toast.success('Candidatura excluída')
      setOpenDeleteDialog(false)
      refreshList()
    } catch (error) {
      toast.error('Erro ao excluir candidatura', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const refreshList = async () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({ ...filters, page })
      .then((response) => {
        setJobApplications(response.data)
        setTotalPages(Math.ceil(response.recordsFiltered / filters.length))
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as candidaturas', error)
      })
      .finally(() => setLoading(false))
  }

  const handleStatusChange = async (newStatus: JobApplicationStatuses, id: number) => {
    setUpdatingStatus([...updatingStatus, id])

    await service.updateStatus(id, newStatus)

    const applications = [...jobApplications]
    const applicationIndex = applications.findIndex((application) => application.id === id)
    applications[applicationIndex].status = newStatus

    setJobApplications(applications)

    setUpdatingStatus(updatingStatus.filter((statusId) => statusId !== id))
    setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== id))
  }

  const openEditModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsCreateModalOpen(true)
    })
  }

  return (
    <>
      <PageTitle title="Banco de Talentos" description="Candidaturas recebidas para as vagas da ABRA" />
      <FilterBar
        onClear={() => {
          setFilters({ ...filters, ...INITIAL_FILTERS })
          dropRpParam()
        }}
        isDirty={isDirty}
      >
        <FilterSearch
          value={filters.search}
          onChange={(search) => setFilters({ ...filters, search })}
          placeholder="Buscar candidato"
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status: status as JobApplicationStatuses })}
          options={Object.entries(statusInfo).map(([key, value]) => ({ value: key, label: value.label }))}
        />
        <FilterSelect
          label="Cargo"
          value={filters.position}
          onChange={(position) => setFilters({ ...filters, position, area: '' })}
          options={positions.map(({ name }) => ({ value: name, label: name }))}
        />
        <FilterField label="RP">
          <SearchableSelect<number>
            size="sm"
            isClearable
            options={requisitions.map((requisition) => ({
              value: requisition.id,
              label: vacancyLabel({ job_position: requisition.name, job_requisition: requisition })
            }))}
            value={filters.requisitionId ?? ''}
            onChange={(requisitionId) => {
              setFilters({ ...filters, requisitionId: requisitionId === '' ? null : requisitionId })
              dropRpParam()
            }}
            placeholder="Todas"
            entity="RP"
            feminine
          />
        </FilterField>
        {(filteredPosition?.areas?.length ?? 0) > 0 && (
          <FilterSelect
            label="Área"
            value={filters.area}
            onChange={(area) => setFilters({ ...filters, area })}
            options={(filteredPosition?.areas ?? []).map((area) => ({ value: area, label: area }))}
          />
        )}
        <FilterSelect
          label="Faixa Salarial"
          emptyLabel="Todas"
          value={filters.salary_range_id}
          onChange={(salary_range_id) => setFilters({ ...filters, salary_range_id })}
          options={salaryRanges.map((salaryRange) => ({ value: salaryRange.id, label: salaryRange.range }))}
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Telefone</Th>
              <Th>E-mail</Th>
              <Th>Vaga</Th>
              <Th>Criado em</Th>
              <Th>Status</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={8} />
            ) : loadError ? (
              <TableErrorRow colSpan={8} entity="candidaturas" feminine onRetry={refreshList} />
            ) : jobApplications.length === 0 ? (
              <TableEmptyRow colSpan={8} entity="candidatura" feminine />
            ) : (
              jobApplications.map((application) => (
                <Tr key={application.id}>
                  <Td>{application.id}</Td>
                  <Td>{application.name}</Td>
                  <Td>{formatPhone(application.phone)}</Td>
                  <Td>
                    <Link href={`mailto:${application.email}`} color="blue.400">
                      {application.email}
                    </Link>
                  </Td>
                  <Td>{vacancyLabel(application)}</Td>
                  <Td>{formatDateTime(application.created_at)}</Td>
                  <Td width="150px" textAlign="left">
                    {isEditingStatus.includes(application.id) ? (
                      <InputGroup display="flex" alignItems="center">
                        <FormSelect
                          size="xs"
                          isDisabled={updatingStatus.includes(application.id)}
                          value={application.status}
                          onChange={(value) => handleStatusChange(value as JobApplicationStatuses, application.id)}
                          options={Object.entries(statusInfo).map(([key, value]) => ({
                            value: key,
                            label: value.label
                          }))}
                          hideEmptyOption
                        />
                        <Tooltip label="Cancelar" aria-label="Cancelar">
                          <Button
                            size="xs"
                            colorScheme="red"
                            ml="1"
                            onClick={() =>
                              setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== application.id))
                            }
                          >
                            <Icon name="lni lni-close" />
                          </Button>
                        </Tooltip>
                      </InputGroup>
                    ) : (
                      <Tooltip label="Clique duas vezes para editar">
                        <StatusBadge
                          status={application.status}
                          statuses={statusInfo}
                          onDoubleClick={() => setIsEditingStatus([...isEditingStatus, application.id])}
                        />
                      </Tooltip>
                    )}
                  </Td>
                  <Td>
                    <ActionButton
                      action="view"
                      label="Detalhes"
                      onClick={() => {
                        setTargetRecord(application)
                        setDetailsDeleteDialog(true)
                      }}
                    />
                    <ActionButton
                      action="download"
                      label="Baixar PDF"
                      isLoading={isDownloading && downloadingId === application.id}
                      isDisabled={isDownloading}
                      onClick={() => {
                        setDownloadingId(application.id)
                        download(() => service.downloadPdf(application.id), 'Erro ao baixar o PDF da candidatura').then(
                          () => setDownloadingId(null)
                        )
                      }}
                    />
                    {permissions.editCandidate && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          openEditModal(application.id)
                        }}
                      />
                    )}
                    {permissions.deleteCandidate && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setTargetRecord(application)
                          setOpenDeleteDialog(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      {targetRecord && (
        <ConfirmDialog
          isOpen={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDelete}
          action="delete"
          entity="candidatura"
          feminine
          name={targetRecord?.name}
          isLoading={isDeleting}
        />
      )}

      {/* Montado sempre e guiado pelo id: montar só depois do fetch era o que fazia o clique em
          "Detalhes" não produzir reação visível nenhuma enquanto a requisição corria. */}
      <JobApplicationViewModal
        id={detailsDeleteDialog && targetRecord ? targetRecord.id : null}
        onClose={() => {
          setDetailsDeleteDialog(false)
        }}
      />

      {permissions.addCandidate && (
        <ActionFab
          label="Adicionar manualmente"
          onClick={() => {
            setRecordEditing(undefined)
            setIsCreateModalOpen(true)
          }}
        />
      )}

      <JobApplicationFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        requisitions={requisitions}
        jobApplication={recordEditing}
        salaryRanges={salaryRanges}
      />
    </>
  )
}
